import React, {
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import DashCardHeading from '../DashCardHeading';
import SnowflakeIcon from 'icons/Snowflake.icon';
import LeadInfo from '../LeadInfo';
import { Button, Loader } from 'ncoded-component-library';
import InfiniteScroll from 'components/InfiniteScroll';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import useColdLeadsInfiniteQuery from '../../hooks/useColdLeadsInfiniteQuery';
import { getHasElementsInArray } from 'utils';
import ArrowupIcon from 'icons/Arrowup.icon';
import SendIcon from 'icons/Send.icon';
import ResolvePopup from 'components/ResolvePopup';
import BroadcastMessageModal from '../BroadcastMessageModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { ColdLeadAggregationResult } from 'router/subrouters/DataRoom/models/Dashboard';
import useMatchMedia from 'hooks/useMatchMedia';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import { ObjectValues } from 'types';

import './ColdLeads.styles.scss';

const SORT_NAME = {
  NAME: 'name',
} as const;

type SortName = ObjectValues<typeof SORT_NAME>;

const SORT_LABEL_COPY: Record<SortName, string> = {
  [SORT_NAME.NAME]: 'name',
};

type ColdLeadsProps = {
  className?: string;
};

const ColdLeads: React.FC<ColdLeadsProps> = (props) => {
  const { className } = props;

  const [sort, setSort] = useState<string>(`-${SORT_NAME.NAME}`);

  const [selectedMember, setSelectedMember] =
    useState<Partial<ColdLeadAggregationResult>>();
  const [broadcastMessageModal, broadcastMessageModalRef] =
    useCallbackRef<ModalRef>();

  const classes = classNames('cold-leads', 'white-panel', className);

  const {
    dataRoom: { _id: dataRoomId, currentMember },
    preferredDashboardTimeframe,
  } = useContext(DataRoomContext);

  const { t } = useTranslation();
  const isPhablet = useMatchMedia('isPhablet');

  const { query, items, totalItems } = useColdLeadsInfiniteQuery({
    dataRoomId,
    timeframe: preferredDashboardTimeframe,
    sort,
  });
  const {
    isLoading,
    isPreviousData,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = query;

  const hasNoLeads = !getHasElementsInArray(items);

  const hasMessagingAccess = currentMember?.useMessaging;

  const selectedMembers = useMemo(
    () => (selectedMember ? [selectedMember] : items),
    [items, selectedMember],
  );

  const handleScroll = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const onClose = useCallback(() => setSelectedMember(null), []);

  const renderSortButton = useCallback(
    (sortName: SortName, content?: ReactNode) => (
      <SortingButton
        content={content}
        sortingValue={sortName}
        sort={sort}
        updateSort={setSort}
      />
    ),
    [sort],
  );

  const renderMessagingAction = useCallback(
    (button: React.ReactNode) => {
      if (hasMessagingAccess) return button;

      return (
        <ResolvePopup
          onHover
          popupContent={t('youDontNotHaveAccessToMessaging')}
        >
          {button}
        </ResolvePopup>
      );
    },
    [hasMessagingAccess, t],
  );

  return (
    <div className={classes}>
      <DashCardHeading
        heading={t('coldLeads')}
        description={t('coldLeadsDescription')}
        icon={SnowflakeIcon}
        numberOfElements={totalItems}
      >
        {renderMessagingAction(
          <Button
            variant="outline"
            disabled={!hasMessagingAccess || hasNoLeads}
            icon={SendIcon}
            onClick={() => {
              if (!hasMessagingAccess || hasNoLeads) return;

              broadcastMessageModal?.open();
            }}
          >
            {!isPhablet && t('broadcastMessage')}
          </Button>,
        )}
      </DashCardHeading>

      {isLoading && hasNoLeads ? (
        <Loader overlay={false} />
      ) : hasNoLeads ? (
        <p className="dashboard__empty">{t('noColdLeadsDescription')}</p>
      ) : (
        <InfiniteScroll
          localScroll
          className="dashboard__scroll"
          onScroll={handleScroll}
        >
          <div className="dashboard__list">
            <div className="dashboard__list__header">
              {Object.values(SORT_NAME).map((sortName) =>
                renderSortButton(sortName, t(SORT_LABEL_COPY[sortName])),
              )}
            </div>

            {items.map((item) => {
              const {
                dataRoomMemberId,
                lastSeen,
                firstName,
                lastName,
                email,
                avatarColor,
              } = item;

              return (
                <div key={dataRoomMemberId} className="dashboard__list__item">
                  <LeadInfo
                    avatarColor={avatarColor}
                    firstName={firstName}
                    lastName={lastName}
                    email={email}
                    lastSeen={lastSeen}
                  />

                  {renderMessagingAction(
                    <Button
                      variant="outline"
                      disabled={!hasMessagingAccess}
                      iconPosition="right"
                      icon={ArrowupIcon}
                      onClick={() => {
                        if (!hasMessagingAccess) return;

                        setSelectedMember(item);
                        broadcastMessageModal?.open();
                      }}
                    >
                      {!isPhablet && t('followUp')}
                    </Button>,
                  )}
                </div>
              );
            })}
          </div>
        </InfiniteScroll>
      )}

      {(isFetchingNextPage || (isPreviousData && !hasNoLeads)) && (
        <Loader overlay={false} />
      )}

      <BroadcastMessageModal
        ref={broadcastMessageModalRef}
        dataRoomMembers={selectedMembers}
        onClose={onClose}
      />
    </div>
  );
};

export default ColdLeads;
